import * as Sentry from '@sentry/svelte';
import Axios, { CancelToken } from 'axios';

import { getBootstrap } from '@client/app/bootstrap';

import { merge } from '@common/utils/objects';
import { CancelablePromise } from '@common/utils/promise';

import { TransportUtils } from './utils';

const { API } = getBootstrap();

const axiosHeaders = {};

const pendingRequests = new Set();

const requestLog = [];
const MAX_LOG_SIZE = 50;

const axios = TransportUtils.axiosCreate({
  baseURL: API,
  headers: {
    ...axiosHeaders
  }
});

// Decide on the real HTTP status, never the `error.status` built below: that one reads
// `response.data.status` and falls back to 400, so a genuine 500 and a dead network both
// come out as 400. 401 and 403 are routine auth noise and stay breadcrumbs only.
//
// `ignoreClientErrors` lets a caller declare that its own 4xx are expected user conditions
// rather than app faults — e.g. login rejecting bad credentials or a missing active domain.
// 5xx and network failures are still captured regardless, since those are real incidents.
export const shouldCaptureApiError = (thrown, { ignoreClientErrors = false } = {}) => {
  if (!thrown?.response) {
    return true;
  }

  const status = thrown.response.status;

  if (status >= 500) {
    return true;
  }

  if (status === 401 || status === 403) {
    return false;
  }

  if (ignoreClientErrors && status >= 400 && status < 500) {
    return false;
  }

  return status >= 400;
};

const generateRequest =
  (method) =>
  ({
    baseURL = API,
    path,
    body,
    query,
    headers = {},
    timeout,
    includeResponseMeta,
    cancellable = true,
    logBody = true,
    // Opt out when a non-2xx is a meaningful answer rather than a fault — probe
    // endpoints that report "not configured" as a 4xx/5xx. Breadcrumbs still record it.
    captureErrors = true,
    // Narrower opt-out than captureErrors: 4xx are expected user conditions here (e.g. a
    // login rejecting bad credentials), but 5xx/network still capture as real incidents.
    ignoreClientErrors = false,
    ...axiosConfig
  }) => {
    const source = CancelToken.source();
    const { context, token } = getBootstrap();
    const data = merge(body || {}, { context });

    const logEntry = {
      method: method.toUpperCase(),
      baseURL,
      path,
      body: logBody ? body || null : body ? '[REDACTED]' : null,
      query: query || null,
      timestamp: new Date().toISOString(),
      response: null
    };

    const request = axios
      .request({
        method,
        baseURL,
        url: path,
        data,
        params: query,
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`
        },
        timeout,
        cancelToken: source.token,
        ...axiosConfig
      })
      .then(
        (response) => {
          logEntry.response = { status: response.status, statusText: response.statusText };
          requestLog.push(logEntry);
          if (requestLog.length > MAX_LOG_SIZE) requestLog.shift();

          if (includeResponseMeta) {
            return response;
          }

          return response.data || true;
        },
        (thrown) => {
          if (!Axios.isCancel(thrown)) {
            const { response } = thrown;

            const error = {};
            error.status = response?.data?.status || 400;
            error.message = response?.data?.message || 'Something went wrong!';

            logEntry.response = { status: error.status, message: error.message };
            requestLog.push(logEntry);
            if (requestLog.length > MAX_LOG_SIZE) requestLog.shift();

            const httpStatus = response?.status;

            Sentry.addBreadcrumb({
              category: 'api',
              type: 'http',
              level: 'error',
              message: `${method.toUpperCase()} ${path}`,
              data: { method: method.toUpperCase(), path, status: httpStatus ?? 'network' }
            });

            if (captureErrors && shouldCaptureApiError(thrown, { ignoreClientErrors })) {
              // `thrown` still carries the stack that this handler is about to discard.
              Sentry.captureException(thrown, {
                // Axios messages are all "Request failed with status code N", so without
                // a fingerprint every API failure collapses into one issue.
                fingerprint: ['api', method.toUpperCase(), path, String(httpStatus ?? 'network')],
                tags: { api_path: path, api_status: httpStatus ?? 'network' },
                // Deliberately no request body — it holds credentials on the login POST.
                extra: { method: method.toUpperCase(), path, baseURL, httpStatus, message: error.message }
              });
            }

            if (error.status === 400) {
              error.message = 'There was a problem with your request. Please try again.';
            }

            if (error.status === 401) {
              error.message = 'You do not have permission to access this page. Please contact your admin.';
            }

            if (error.status === 403) {
              error.message = 'Your email has not been verified. Please check your inbox.';
            }

            return error;
          }
        }
      );

    let tracked;

    const cancelable = CancelablePromise(
      request.finally(() => {
        if (cancellable && tracked) {
          pendingRequests.delete(tracked);
        }
      }),
      () => {
        if (cancellable && tracked) {
          pendingRequests.delete(tracked);
        }
        source.cancel('Canceled');
      }
    );

    tracked = { cancelable, label: path };

    if (cancellable) {
      pendingRequests.add(tracked);
    }

    return cancelable;
  };

export const APITransport = {
  get: generateRequest('get'),
  post: generateRequest('post'),
  put: generateRequest('put'),
  delete: generateRequest('delete'),
  cancelPendingRequests: (reason = 'route-change') => {
    pendingRequests.forEach(({ cancelable, label }) => {
      if (cancelable?.cancel) {
        cancelable.cancel();
        console.info('Request cancelled', { label, reason });
      }
    });

    pendingRequests.clear();
  },
  getRequestLog: () => [...requestLog],
  clearRequestLog: () => {
    requestLog.length = 0;
  }
};
